import * as React from 'react';
import { MapMouseEvent, useMap } from 'react-map-gl/mapbox';

import { Mouse } from '@longline/aqua-ui/controls/Mouse';
import { Key } from '@longline/aqua-ui/controls/Key';

import { CircleEditorPoint } from './CircleEditorPoint';
import { CircleEditorSurface } from './CircleEditorSurface';
import { IPoint } from '../../types/IPoint';
import { Guide } from '../../ui/Guide';
import { MAX_STRUCTURE_RADIUS } from '../../types/Constants';

interface ICircleEditorProps {
  /** Circle center point */
  point: IPoint;
  /** Circle radius (meters) */
  radius: number;
  /** Fired when polygon's points change. */
  onChange: (point: IPoint, radius: number) => void;
  /** Fired when polygon is deleted. */
  onDelete: () => void;
  /** Fired when drawing mode is canceled. */
  onCancel: () => void;
}

const CircleEditor = (props: ICircleEditorProps) => {
  const map = useMap().current!;

  const [point, setPoint] = React.useState(props.point);
  const [radius, setRadius] = React.useState(props.radius);

  React.useEffect(() => {
    if (props.point.lat !== point.lat || props.point.lng !== point.lng) {
      setPoint(props.point);
    }
    if (props.radius !== radius) {
      setRadius(props.radius);
    }
  }, [props.point, props.radius]);

  const handleKeydown = (e: KeyboardEvent) => {
    switch(e.code) {
      case 'Escape':
      case 'Backspace':
        map.getCanvas().style.cursor = '';
        props.onCancel();
        break;
      case 'NumpadDecimal':
      case 'Delete':
        map.getCanvas().style.cursor = '';
        props.onDelete();
        break;        
    }
  }   

  const handleClick = (e: MapMouseEvent) => {
    // Shift+click starts box-select, don't interfere
    if (e.originalEvent.shiftKey) return;

    // Ctrl+click on the selected structure deselects it
    if(e.defaultPrevented) {
      const additive = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
      if (additive) {
        props.onCancel();
      }
      return;
    }
    props.onCancel();
  }

  const mount = () => {
    // Start listening to keyboard:
    document.addEventListener('keydown', handleKeydown);            
    map.on('click', handleClick);
  }

  const unmount = () => {
    // Stop listening to keyboard:
    document.removeEventListener('keydown', handleKeydown);            
    map.off('click', handleClick);
  }

  // Mounting/unmounting must only happen once:
  React.useEffect(() => {
    mount();
    return unmount
  }, []);

  const handleChange = (newPoint: IPoint, newRadius: number) => {
    const r = Math.min(MAX_STRUCTURE_RADIUS, newRadius);
    setPoint(newPoint);
    setRadius(r);
    props.onChange(newPoint, r);
  }

  return (
    <>
      <Guide x={16} y={64} title="Editing a circular structure">
        <ul>
          <li>Click <Mouse size={14} left/> and drag the dot on the circle's circumference to adjust its size. <Key value="ESC"/> cancels.</li>
          <li>Click <Mouse size={14} left/> the circle's surface and drag to move the circle.</li>
          <li><Key value="CTRL"/> <Key value="C"/>, <Key value="V"/> copies.</li>
          <li>Use <Key value="CTRL"/><Mouse size={14} left/> to multi-select.</li>
        </ul>
      </Guide>
      <CircleEditorSurface point={point} radius={radius} onChange={handleChange} />
      <CircleEditorPoint   point={point} radius={radius} onChange={handleChange}/>
    </>
  );
}

export { CircleEditor, ICircleEditorProps }
